/**
 * #147 · Herramientas de XYVONA: la página del impresor y los permisos.
 *
 * Dos cosas que sólo hace el equipo de XYVONA y que hasta ahora vivían en la
 * terminal de quien tuviera el repo:
 *
 * 1. **Bajar `xyvona_decrypt.html`**, la página con la que el impresor abre el
 *    `.xyvc1`. Se le manda UNA vez y la guarda; lo que cambia en cada pedido
 *    son el archivo y la clave. Por eso importa la versión: el día que haya
 *    que reenviarla, la pregunta es "¿cuál tenés?", y sin un número esa
 *    conversación no termina.
 *
 * 2. **Otorgar y revocar `print_file:reveal_key`.** Es el permiso que habilita
 *    el botón de la clave en el detalle de una orden. Se otorga de a uno, con
 *    motivo, y queda auditado. La base impide otorgárselo a un usuario de un
 *    cliente: eso no lo decide esta pantalla ni la API, lo decide el trigger
 *    de `0027_staff_permissions.sql`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { DetailList, DetailRow } from '../products/ui/fields';
import { hqKeys } from './keys';
import { downloadSupplierTool } from './supplierTool';

type Tool = Schemas['SupplierToolOut'];
type Staff = Schemas['StaffUserOut'];
type Granted = Schemas['PermissionGrantedOut'];

/** El único permiso que hay hoy. El catálogo lo confirma contra la API. */
const REVEAL_KEY = 'print_file:reveal_key';

/** Los motivos de la lista cerrada de la API (#173). «Otro» exige detalle. */
const REASON_KINDS = ['reprint', 'printer_claim', 'audit', 'other'] as const;
type ReasonKind = (typeof REASON_KINDS)[number];
/** Plazos ofrecidos, en horas, con su texto. El default es el de la API (24 h). */
const DURATIONS: readonly { hours: number; labelKey: MessageKey }[] = [
  { hours: 4, labelKey: 'tooling.permissions.duration.4' },
  { hours: 24, labelKey: 'tooling.permissions.duration.24' },
  { hours: 72, labelKey: 'tooling.permissions.duration.72' },
  { hours: 24 * 7, labelKey: 'tooling.permissions.duration.168' },
  { hours: 24 * 30, labelKey: 'tooling.permissions.duration.720' },
];
const REASON_LABEL_KEYS: Record<ReasonKind, MessageKey> = {
  reprint: 'tooling.permissions.reason.reprint',
  printer_claim: 'tooling.permissions.reason.printer_claim',
  audit: 'tooling.permissions.reason.audit',
  other: 'tooling.permissions.reason.other',
};

interface Borrador {
  kind: ReasonKind;
  note: string;
  hours: number;
}
const BORRADOR: Borrador = { kind: 'reprint', note: '', hours: 24 };

export function ToolingPage(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();
  const [borrador, setBorrador] = useState<Record<number, Borrador>>({});

  const tool = useApiQuery<Tool>(hqKeys.supplierTool(), '/api/v1/backoffice/supplier-tool');
  const staff = useApiQuery<Staff[]>(hqKeys.staff(), '/api/v1/backoffice/staff-permissions');

  const bajar = useMutation<string, unknown, string>({
    mutationFn: (filename) => downloadSupplierTool(filename, { api }),
  });

  const otorgar = useMutation<Granted, unknown, { userId: number; draft: Borrador }>({
    mutationFn: ({ userId, draft }) =>
      api.post<Granted>('/api/v1/backoffice/staff-permissions', {
        user_id: userId,
        permission: REVEAL_KEY,
        reason_kind: draft.kind,
        note: draft.note.trim() === '' ? null : draft.note.trim(),
        expires_in_hours: draft.hours,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hqKeys.staff() }),
  });

  const revocar = useMutation<Granted, unknown, number>({
    mutationFn: (userId) =>
      api.delete<Granted>(
        `/api/v1/backoffice/staff-permissions/${String(userId)}/${REVEAL_KEY}`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hqKeys.staff() }),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader
          title={t('tooling.supplier.title')}
          description={t('tooling.supplier.description')}
        />
        <CardBody className="space-y-4">
          <QueryBoundary query={tool}>
            {(data) => (
              <>
                <DetailList>
                  <DetailRow
                    label={t('tooling.supplier.version')}
                    value={<code className="font-mono">{data.version}</code>}
                  />
                  <DetailRow label={t('tooling.supplier.bytes')} value={fmt.number(data.bytes)} />
                </DetailList>
                <div>
                  <p className="text-xs tracking-wide text-ink-subtle uppercase">
                    {t('tooling.supplier.sha256')}
                  </p>
                  <code className="mt-0.5 block font-mono text-xs break-all text-ink select-all">
                    {data.sha256}
                  </code>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    disabled={bajar.isPending}
                    onClick={() => {
                      bajar.mutate(data.filename);
                    }}
                  >
                    {bajar.isPending
                      ? t('tooling.supplier.downloading')
                      : t('tooling.supplier.download')}
                  </Button>
                  <span className="text-xs text-ink-subtle">{t('tooling.supplier.sendOnce')}</span>
                </div>
                {bajar.isError ? <ErrorState error={bajar.error} /> : null}
              </>
            )}
          </QueryBoundary>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('tooling.permissions.title')}
          description={t('tooling.permissions.description')}
        />
        <CardBody className="p-0">
          <QueryBoundary query={staff} empty={{ title: t('tooling.permissions.empty') }}>
            {(filas) => (
              <Table caption={t('tooling.permissions.title')}>
                <thead>
                  <tr>
                    <Th>{t('tooling.permissions.col.person')}</Th>
                    <Th>{t('tooling.permissions.col.permission')}</Th>
                    <Th className="text-right">{t('tooling.permissions.col.action')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((persona) => {
                    // `permissions` es opcional en el schema generado: la
                    // función SQL devuelve `{}` para quien no tiene ninguno,
                    // pero el tipo no lo garantiza y un `undefined` acá
                    // apagaría el botón de otorgar sin decir por qué.
                    const tiene = (persona.permissions ?? []).includes(REVEAL_KEY);
                    // La concesión, vigente o vencida: la vencida se muestra
                    // como historia ("venció el 12") y no como permiso.
                    const concesion = (persona.grants ?? []).find(
                      (grant) => grant.permission === REVEAL_KEY,
                    );
                    const draft = borrador[persona.user_id] ?? BORRADOR;
                    const draftListo = draft.kind !== 'other' || draft.note.trim() !== '';
                    return (
                      <tr key={persona.user_id}>
                        <Td>
                          <span className="font-medium text-ink">
                            {persona.full_name ?? persona.email}
                          </span>
                          <span className="block text-xs text-ink-subtle">{persona.email}</span>
                        </Td>
                        <Td>
                          {tiene ? (
                            <span className="flex flex-col gap-0.5">
                              <Badge tone="suspect">{t('tooling.permissions.granted')}</Badge>
                              {concesion?.expires_at !== null && concesion?.expires_at !== undefined && (
                                <span className="text-xs text-ink-subtle">
                                  {t('tooling.permissions.expires', {
                                    when: fmt.dateTime(concesion.expires_at),
                                  })}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="flex flex-col gap-0.5 text-xs text-ink-subtle">
                              <span>{t('tooling.permissions.none')}</span>
                              {concesion?.expires_at !== null && concesion?.expires_at !== undefined && (
                                <span>
                                  {t('tooling.permissions.expired', {
                                    when: fmt.dateTime(concesion.expires_at),
                                  })}
                                </span>
                              )}
                            </span>
                          )}
                        </Td>
                        <Td className="text-right">
                          {tiene ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={revocar.isPending}
                              onClick={() => {
                                revocar.mutate(persona.user_id);
                              }}
                            >
                              {t('tooling.permissions.revoke')}
                            </Button>
                          ) : (
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <select
                                aria-label={t('tooling.permissions.reason')}
                                value={draft.kind}
                                onChange={(e) => {
                                  const kind = e.target.value;
                                  if (!(REASON_KINDS as readonly string[]).includes(kind)) return;
                                  setBorrador((b) => ({
                                    ...b,
                                    [persona.user_id]: { ...draft, kind: kind as ReasonKind },
                                  }));
                                }}
                                className="h-9 rounded border border-line bg-surface px-2 text-sm text-ink"
                              >
                                {REASON_KINDS.map((kind) => (
                                  <option key={kind} value={kind}>
                                    {t(REASON_LABEL_KEYS[kind])}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label={t('tooling.permissions.duration')}
                                value={String(draft.hours)}
                                onChange={(e) => {
                                  setBorrador((b) => ({
                                    ...b,
                                    [persona.user_id]: { ...draft, hours: Number(e.target.value) },
                                  }));
                                }}
                                className="h-9 rounded border border-line bg-surface px-2 text-sm text-ink"
                              >
                                {DURATIONS.map((plazo) => (
                                  <option key={plazo.hours} value={String(plazo.hours)}>
                                    {t(plazo.labelKey)}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={draft.note}
                                placeholder={t(
                                  draft.kind === 'other'
                                    ? 'tooling.permissions.noteRequired'
                                    : 'tooling.permissions.note',
                                )}
                                onChange={(e) => {
                                  setBorrador((b) => ({
                                    ...b,
                                    [persona.user_id]: { ...draft, note: e.target.value },
                                  }));
                                }}
                                className="h-9 w-56 rounded border border-line bg-surface px-2 text-sm text-ink"
                              />
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={otorgar.isPending || !persona.is_active || !draftListo}
                                onClick={() => {
                                  otorgar.mutate({ userId: persona.user_id, draft });
                                }}
                              >
                                {t('tooling.permissions.grant')}
                              </Button>
                            </div>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </QueryBoundary>
          {otorgar.isError ? (
            <div className="p-4">
              <ErrorState error={otorgar.error} />
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
